import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MessageCircle, ExternalLink, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { Skeleton, EmptyState, StatusPill, ClearFiltersButton } from "../../components/common";
import { formatDateRange, formatINRAbbrev, formatByType } from "../../lib/format";
import CommentsDrawer from "./CommentsDrawer";

export default function BrandCampaignsPage() {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [filters, setFilters] = useState({ q: "", status: "", billing: "", month: "", sort: "recent" });
  const [drawerCampaign, setDrawerCampaign] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/brand/campaigns"); setCampaigns(data.campaigns); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // URL persistence
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const next = { ...filters };
    Object.keys(next).forEach((k) => { if (params.get(k)) next[k] = params.get(k); });
    setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const qs = params.toString();
    window.history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
  }, [filters]);

  const filtered = useMemo(() => {
    let arr = campaigns.filter((c) => {
      if (filters.q && !c.campaign_name.toLowerCase().includes(filters.q.toLowerCase())) return false;
      if (filters.status && c.status !== filters.status) return false;
      if (filters.billing && (c.tracker_values?.billing_status !== filters.billing)) return false;
      if (filters.month && (c.tracker_values?.month || "").toLowerCase() !== filters.month.toLowerCase()) return false;
      return true;
    });
    if (filters.sort === "recent") arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    if (filters.sort === "name") arr.sort((a, b) => a.campaign_name.localeCompare(b.campaign_name));
    return arr;
  }, [campaigns, filters]);

  const monthOpts = useMemo(() => [...new Set(campaigns.map((c) => c.tracker_values?.month).filter(Boolean))], [campaigns]);

  return (
    <div data-testid="brand-campaigns-page">
      <div className="flex items-end justify-between mb-2">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Your campaigns</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Each tile is one campaign. Click Tracker for live performance.</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium" data-testid="brand-campaign-count">{campaigns.length} campaigns</div>
          <div className="text-xs text-[var(--text-secondary)] mt-0.5">Data refreshes every 6 hours</div>
        </div>
      </div>

      <div className="wl-card-solid p-4 mt-6 mb-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          <input className="wl-input pl-9" placeholder="Search campaigns" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="brand-search" />
        </div>
        <select className="wl-input w-auto" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="brand-filter-status">
          <option value="">Status</option>{["Active","Pending","Completed","Expired"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="wl-input w-auto" value={filters.billing} onChange={(e) => setFilters({ ...filters, billing: e.target.value })} data-testid="brand-filter-billing">
          <option value="">Billing</option>{["Pending","Paid","Overdue"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="wl-input w-auto" value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })} data-testid="brand-filter-month">
          <option value="">Month</option>{monthOpts.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="wl-input w-auto" value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })} data-testid="brand-filter-sort">          <option value="recent">Sort: Most recent</option>
          <option value="name">Sort: Name A–Z</option>
        </select>
        <ClearFiltersButton filters={filters} defaults={{ q: "", status: "", billing: "", month: "", sort: "recent" }} onClear={setFilters} testid="brand-clear-filters" />
      </div>

      {loading ? <div className="grid grid-cols-3 gap-5">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-80" />)}</div>
      : filtered.length === 0 ? <EmptyState title={campaigns.length === 0 ? "No campaigns assigned yet." : "No results."} subtitle={campaigns.length === 0 ? "Once campaigns are assigned to your team, they will appear here." : "Adjust filters or search to find campaigns."} />
      : (
        <div className="grid grid-cols-3 gap-5" data-testid="brand-cards">
          {filtered.map((c) => <CampaignCard key={c.id} c={c} onComments={() => setDrawerCampaign(c)} onTracker={() => nav(`/campaigns/${c.id}/tracking`)} />)}
        </div>
      )}

      {drawerCampaign && <CommentsDrawer campaign={drawerCampaign} onClose={() => setDrawerCampaign(null)} onMarkedRead={load} />}
    </div>
  );
}

function CampaignCard({ c, onComments, onTracker }) {
  const tv = c.tracker_values || {};
  const month = (tv.month || "").toUpperCase();
  const billingValue = tv.billing_status;

  // Card content is now driven by the per-campaign tracker_config so the toggles
  // in the configurator (show_in_card + visible_to_brand) actually take effect.
  // Special-cased fields keep their richer presentation: start/end dates collapse
  // to a single "Dates" row, billing_status is a pill at the bottom, brief_link is
  // a link in the footer, month is shown above the title.
  const fields = (c.tracker_config?.fields || [])
    .filter((f) => f.visible_to_brand !== false && f.show_in_card === true)
    .filter((f) => !["month", "billing_status", "brief_link"].includes(f.id))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Collapse start_date + end_date into one "Dates" row if either is shown in card.
  const dateRangeShown = fields.some((f) => f.id === "start_date" || f.id === "end_date");
  const dateRange = (tv.start_date || tv.end_date) ? formatDateRange(tv.start_date, tv.end_date) : null;
  const rowsToRender = fields
    .filter((f) => f.id !== "start_date" && f.id !== "end_date");

  const renderValue = (f) => {
    const raw = tv[f.id];
    if (raw === undefined || raw === null || raw === "") return "—";
    if (f.type === "currency") return formatINRAbbrev(raw);
    return formatByType(raw, f.type === "url" ? "text" : f.type);
  };

  const monthVisible = (c.tracker_config?.fields || []).some(
    (f) => f.id === "month" && f.visible_to_brand !== false
  );
  const billingVisible = (c.tracker_config?.fields || []).some(
    (f) => f.id === "billing_status" && f.visible_to_brand !== false
  );
  const briefVisible = (c.tracker_config?.fields || []).some(
    (f) => f.id === "brief_link" && f.visible_to_brand !== false
  );

  return (
    // Fixed minimum height + flex column so the card stays the same size regardless
    // of how many tracker fields are toggled on. Header (month + status), title, and
    // footer (billing + actions) are anchored; the body distributes itself in the
    // remaining space via `flex-1` + `justify-around` so 1 row, 3 rows, or 6 rows
    // all look intentional.
    <div className="wl-card p-5 flex flex-col gap-4 min-h-[20rem] transition-all hover:shadow-lg hover:-translate-y-0.5" style={{ borderColor: "var(--border-dashed)" }} data-testid={`brand-card-${c.campaign_id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold tracking-[0.14em] text-[var(--brand)]" data-testid={`card-month-${c.campaign_id}`}>{monthVisible ? (month || "—") : "\u00A0"}</div>
        <StatusPill value={c.status} />
      </div>

      <h3 className="font-display text-lg font-semibold leading-tight" data-testid={`card-name-${c.campaign_id}`}>{c.campaign_name}</h3>

      {(dateRangeShown || rowsToRender.length > 0) ? (
        <div className="flex-1 flex flex-col justify-around text-sm">
          {dateRangeShown && (
            <div className="flex justify-between gap-3">
              <span className="text-[var(--text-secondary)] text-xs">Dates</span>
              <span className="font-medium text-right truncate">{dateRange || "—"}</span>
            </div>
          )}
          {rowsToRender.map((f) => (
            <div key={f.id} className="flex justify-between gap-3" data-testid={`card-row-${f.id}`}>
              <span className="text-[var(--text-secondary)] text-xs">{f.name}</span>
              <span className="font-medium text-right truncate max-w-[55%]" title={tv[f.id]}>{renderValue(f)}</span>
            </div>
          ))}
        </div>
      ) : (
        // No body rows configured — still occupy the body slot so the footer doesn't
        // ride up against the title.
        <div className="flex-1 flex items-center justify-center text-xs text-[var(--text-secondary)] italic">No card fields configured</div>
      )}

      {billingVisible && (
        <div className="flex items-center justify-between pt-3 border-t border-dashed border-[var(--border-dashed)]">
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--text-secondary)]">Billing</span>
            <StatusPill value={billingValue} type="billing" />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-auto pt-1">
        <div className="flex items-center gap-3 text-sm">
          {briefVisible && tv.brief_link && <a href={tv.brief_link} target="_blank" rel="noreferrer" className="text-[var(--brand)] inline-flex items-center gap-1 font-medium hover:underline">Brief <ExternalLink size={11} /></a>}
          <button onClick={onComments} className={`wl-btn-ghost inline-flex items-center gap-1.5 text-xs relative ${c.unread_comment_count ? "font-bold text-[var(--brand)]" : ""}`} data-testid={`card-comments-${c.campaign_id}`} title={c.unread_comment_count ? `${c.unread_comment_count} new comment${c.unread_comment_count === 1 ? "" : "s"}` : "View comments"}>
            <MessageCircle size={13} /> {c.comment_count || 0}
            {c.unread_comment_count > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-[var(--brand)] text-white" data-testid={`card-unread-${c.campaign_id}`}>{c.unread_comment_count}</span>
            )}
          </button>
        </div>
        <button onClick={onTracker} disabled={!c.google_sheet_id} className="wl-btn-secondary inline-flex items-center gap-1.5 text-xs disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`card-tracker-${c.campaign_id}`}>
          Open Tracker <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}
